import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Card, ClickAwayListener, TextField, IconButton, Checkbox, FormControlLabel, Drawer } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import info from "../../../assets/info.svg";
import { useTheme } from '@mui/material';

const AddDiscoveryAgent: React.FC<any> = ({ isOpen, onClose, selectedAgent, onSubmit }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [formData, setFormData] = useState({ agentName: "", description: "", cidrBlock: "", });
  const [touched, setTouched] = useState({ agentName: false, description: false, cidrBlock: false });
  const [enableDiscovery, setEnableDiscovery] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFormData, setInitialFormData] = useState({ agentName: "", description: "", cidrBlock: "", enableDiscovery: false });
  
  // Prefill form fields if selectedAgent is present
  useEffect(() => {
    if (selectedAgent) {
      const newFormData = {
        agentName: selectedAgent.name || "",
        description: selectedAgent.description || "",
        cidrBlock: Array.isArray(selectedAgent.ip_range)
          ? selectedAgent.ip_range.join(", ")
          : selectedAgent.cidr_maping || "",
      };
      const newEnableDiscovery = selectedAgent.discovery === true;

      setFormData(newFormData);
      setEnableDiscovery(newEnableDiscovery);
      setInitialFormData({ ...newFormData, enableDiscovery: newEnableDiscovery });
      setHasUnsavedChanges(false);
    } else {
      const emptyFormData = { agentName: "", description: "", cidrBlock: "" };
      setFormData(emptyFormData);
      setEnableDiscovery(false);
      setInitialFormData({ ...emptyFormData, enableDiscovery: false });
      setHasUnsavedChanges(false);
    }
  }, [selectedAgent, isOpen]);

  // Track form changes
  useEffect(() => {
    const currentData = { ...formData, enableDiscovery };
    const hasChanges = JSON.stringify(currentData) !== JSON.stringify(initialFormData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, enableDiscovery, initialFormData]);

  // useEffect(() => {
  //   const handleEsc = (event: any) => {
  //     if (event.key === "Escape") {
  //       handleDrawerClose();
  //     }
  //   };
  //   document.addEventListener("keydown", handleEsc);
  //   return () => {
  //     document.removeEventListener("keydown", handleEsc);
  //   };
  // }, [onClose]);

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleDrawerClose = () => {
    if (hasUnsavedChanges) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  const handleBackgroundClick = () => {
    if (hasUnsavedChanges) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = (confirm: boolean) => {
    if (confirm) {
      // Reset form and close both dialog + drawer
      setFormData(initialFormData);
      setEnableDiscovery(initialFormData.enableDiscovery);
      setTouched({ agentName: false, description: false, cidrBlock: false });
      setHasUnsavedChanges(false);
      setConfirmDialogOpen(false);
      onClose();
    } else {
      // Just close confirmation dialog
      setConfirmDialogOpen(false);
    }
  };

  const handleSubmit = () => {
    const dataToSend = {
      name: formData.agentName,
      description: formData.description,
      agent_type: "on_prem",
      ip_range: formData.cidrBlock
        .split(',')
        .map(v => v.trim())
        .filter(v => v.length > 0),
      discovery: enableDiscovery,
    };
    onSubmit(dataToSend);
    onClose();
  };
  const alphaNumPattern = /^[a-zA-Z0-9 ]*$/;

  const isFormValid = () => {
    const agentNameValid =
      formData.agentName.trim() !== "" &&
      formData.agentName.trim().length >= 5 &&
      formData.agentName.trim().length <= 18 &&
      alphaNumPattern.test(formData.agentName);

    const descriptionValid =
      formData.description.trim() !== "" &&
      formData.description.trim().length >= 5 &&
      formData.description.trim().length <= 50 &&
      alphaNumPattern.test(formData.description);

    const cidrValid = 
      formData.cidrBlock.trim() !== "" &&
      isValidIpOrCidr(formData.cidrBlock);

    return agentNameValid && descriptionValid && cidrValid;
  };
  // Add this function before return
  const isValidIpOrCidr = (value: string) => {
    // Allow multiple IPs/CIDRs separated by comma
    // Matches IPv4 and IPv4/CIDR (e.g., 10.0.0.0 or 10.0.0.0/24)
    const ipOrCidrPattern = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/([0-9]|[1-2][0-9]|3[0-2]))?$/;
    return value
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0)
      .every(v => ipOrCidrPattern.test(v));
  };
  if (!isOpen) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleBackgroundClick}
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: "320px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            zIndex: 1300,
          }}
        >
          <Card
            sx={{
              width: "100%",
              height: "97vh",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              borderRadius: "16px",
              boxShadow: 3,
              position: "relative",
            }}
          >
            <div className="cloud-scan-header">
              <div className="header-left">
                <p className="header-title"> {selectedAgent ? "Edit On-prem Agent" : "On-prem Agent"} </p>
                <p className="header-text">{selectedAgent ? "Update agent details" : "Provide agent details"} </p>
              </div>
              <IconButton
                onClick={handleDrawerClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <div style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%' }}>
              <div>
                <TextField
                  label="Agent Name"
                  name="agentName"
                  value={formData.agentName}
                  onChange={handleFormChange}
                  onBlur={() => handleBlur("agentName")}
                  fullWidth
                  required
                  margin="normal"
                  inputProps={{ minLength: 5, maxLength: 18 }}
                  error={
                    touched.agentName &&
                    (
                      formData.agentName.trim().length < 5 ||
                      formData.agentName.trim().length > 18 ||
                      !alphaNumPattern.test(formData.agentName)
                    )
                  }
                  helperText={
                    touched.agentName &&
                    (
                      formData.agentName.trim().length < 5
                        ? "Name must be at least 5 characters."
                        : formData.agentName.trim().length > 18
                          ? "Maximum 18 characters allowed"
                          : !alphaNumPattern.test(formData.agentName)
                            ? "Special characters not allowed"
                            : ""
                    )
                  }
                />
                <TextField
                  label="Agent Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  onBlur={() => handleBlur("description")}
                  fullWidth
                  required
                  margin="normal"
                  inputProps={{ minLength: 5, maxLength: 50 }}
                  error={
                    touched.description &&
                    (
                      formData.description.trim().length < 5 ||
                      formData.description.trim().length > 50 ||
                      !alphaNumPattern.test(formData.description)
                    )
                  }
                  helperText={
                    touched.description &&
                    (
                      formData.description.trim().length < 5
                        ? "Description must be at least 5 characters."
                        : formData.description.trim().length > 50
                          ? "Maximum 50 characters allowed"
                          : !alphaNumPattern.test(formData.description)
                            ? "Special characters not allowed"
                            : ""
                    )
                  }
                />
                <TextField
                  label="Customer CIDR Block"
                  name="cidrBlock"
                  value={formData.cidrBlock}
                  onChange={handleFormChange}
                  onBlur={() => handleBlur("cidrBlock")} // Mark as touched on blur
                  fullWidth
                  required
                  margin="normal"
                  error={
                    touched.cidrBlock &&
                    (
                      formData.cidrBlock.trim() === "" ||
                      !isValidIpOrCidr(formData.cidrBlock)
                    )
                  }
                  helperText={
                    touched.cidrBlock &&
                    (
                      formData.cidrBlock.trim() === ""
                        ? "CIDR Block is required"
                        : !isValidIpOrCidr(formData.cidrBlock)
                          ? "Invalid CIDR/IP format"
                          : ""
                    )
                  }
                />
                <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'left', marginTop: '8px' }}>
                  <img style={{ width: '22px', height: '22px', marginRight: '5px' }} src={info} alt="Info" />
                  <span style={{ fontWeight: '400', fontSize: '12px', color: isDarkMode ? "#fff" : "#8C8FB1" }}>Add multiple CIDR block using comma (,).</span>
                </p>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableDiscovery}
                      onChange={e => {
                        setEnableDiscovery(e.target.checked);
                      }}
                    />
                  }
                  label={<span style={{ fontWeight: '700', fontSize: '12px', color: isDarkMode ? "#fff" : "#10111E" }}>Enable Discovery</span>}
                  sx={{ marginTop: '8px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                {(
                  selectedAgent && <Button
                    variant="outlined"
                    onClick={handleDrawerClose}
                    sx={{ width: "40%" }}
                  >
                    Cancel
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={
                    !isFormValid() ||
                    (touched.agentName && (
                      formData.agentName.trim().length < 5 ||
                      formData.agentName.trim().length > 18
                    )) ||
                    (touched.description && (
                      formData.description.trim().length < 5 ||
                      formData.description.trim().length > 50
                    )) ||
                    (touched.cidrBlock && !isValidIpOrCidr(formData.cidrBlock))
                  }
                  sx={{ width: selectedAgent ? '40%' : '100%' }}
                >
                  {selectedAgent ? "Save" : "Create Agent"}
                </Button>
              </div>
            </div>
          </Card>
        </Box>
      </Drawer>


      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false)
        }}
        sx={{ height: "100%", width: "100%" }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>Unsaved Changes</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          This action will cancel the Agent initiated.
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={() => handleConfirmClose(true)}
            variant="contained"
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleConfirmClose(false)}
            variant="outlined"
            fullWidth
          >
            Go Back
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddDiscoveryAgent;
